import { useEffect, useState } from 'preact/hooks';
import { getRecordTypeSchema, listAllRecordsWithLinks, type RecordRow } from '../lib/api';
import { useRecordsVersion } from '../lib/recordEditor';
import { activeOnly } from '../lib/retired';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import { humanizeValue } from '../lib/fieldDisplay';
import { Strip } from '../components/Strip';
import { RecordImage } from '../components/RecordMedia';
import { markIsDark, measureMarkLuminance } from '../lib/markLuminance';
import {
  AA,
  LINK,
  PROJECT_LENSES,
  matchesLens,
  otherEnd,
  projectLens,
  spineFromLifecycle,
  type ProjectLens,
} from '../lib/auditionSpread';
import './spread.css';

// A4 — PROJECTS: which productions are we in, and where has each got to.
//
// ===================================================================
// 2026-09-19 — READ TENANT-WIDE, FULL STOP
// ===================================================================
// This screen used to read through lib/subjectScope.ts, which decided
// between a household-wide list and one performer's. That file is gone: an
// account is one career (Huan's ruling), so every read here is simply this
// tenant's own — there is no household to fall back to and no performer to
// narrow into.
//
// ===================================================================
// THE LENS STRIP IS DERIVED, BECAUSE aa_project HAS NO LIFECYCLE
// ===================================================================
// The July spec gave the old Actor pack a project PHASE — Auditioned /
// Booked / Released — as its own lifecycle. aa_project has neither a
// lifecycle nor a phase field, and `released` has no representation anywhere
// in this pack. Rendering the old strip here would filter on states that do
// not exist.
//
// Giving the project a lifecycle is a MANIFEST CHANGE, and the manifest is
// immutable per version (migration 0033) — amending it means bumping
// authority.version, which is a decision with a migration, not a strip. So
// the two lenses below are computed from what the pack does say, in
// lib/auditionSpread.ts's projectLens, and no state name appears in either
// file.

export interface HouseholdProjectsProps {
  tenantId: string;
  onOpen: (projectId: string) => void;
}

interface Line {
  project: RecordRow;
  roles: number;
  reads: number;
  lens: ReturnType<typeof projectLens>;
}

export function HouseholdProjects({ tenantId, onOpen }: HouseholdProjectsProps) {
  const [lines, setLines] = useState<Line[] | null>(null);
  // Which logo tiles carry a DARK mark and want a light ground — asked of the
  // picture itself when it loads (lib/markLuminance).
  const [darkMarks, setDarkMarks] = useState<Record<string, boolean>>({});
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [lens, setLens] = useState<ProjectLens>('all');
  const [retryNonce, setRetryNonce] = useState(0);
  const recordsVersion = useRecordsVersion();

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setFailure(null);
      try {
        const [auditionSchema, projects, allRoles, auds, engs] = await Promise.all([
          getRecordTypeSchema(tenantId, AA.AUDITION).catch(() => null),
          listAllRecordsWithLinks(tenantId, AA.PROJECT, { linkTypes: [LINK.ANTHOLOGY_PARENT] }),
          listAllRecordsWithLinks(tenantId, AA.ROLE, { linkTypes: [LINK.ROLE_PROJECT] }),
          listAllRecordsWithLinks(tenantId, AA.AUDITION, { linkTypes: [LINK.AUDITION_ROLE] }),
          listAllRecordsWithLinks(tenantId, AA.ENGAGEMENT, { linkTypes: [LINK.ENGAGEMENT_ROLE] }),
        ]);
        if (cancelled) return;

        const spine = spineFromLifecycle(auditionSchema?.lifecycle ?? undefined).spine;
        const roles = activeOnly(allRoles.records);
        const roleProject = new Map(
          roles.map((r) => [r.id, otherEnd(allRoles.linksByRecordId[r.id] ?? [], r.id, LINK.ROLE_PROJECT)])
        );

        const statesByProject = new Map<string, (string | null)[]>();
        for (const a of activeOnly(auds.records)) {
          const roleId = otherEnd(auds.linksByRecordId[a.id] ?? [], a.id, LINK.AUDITION_ROLE);
          const pid = roleId ? roleProject.get(roleId) ?? null : null;
          if (!pid) continue;
          const list = statesByProject.get(pid);
          if (list) list.push(a.lifecycle_state);
          else statesByProject.set(pid, [a.lifecycle_state]);
        }
        const engByProject = new Map<string, number>();
        for (const e of activeOnly(engs.records)) {
          const roleId = otherEnd(engs.linksByRecordId[e.id] ?? [], e.id, LINK.ENGAGEMENT_ROLE);
          const pid = roleId ? roleProject.get(roleId) ?? null : null;
          if (!pid) continue;
          engByProject.set(pid, (engByProject.get(pid) ?? 0) + 1);
        }

        const next: Line[] = activeOnly(projects.records).map((project) => {
          const states = statesByProject.get(project.id) ?? [];
          const engagements = engByProject.get(project.id) ?? 0;
          return {
            project,
            roles: roles.filter((r) => roleProject.get(r.id) === project.id).length,
            reads: states.length,
            lens: projectLens(spine, states, engagements),
          };
        });
        // ⚑ BOOKED RANKS FIRST, then newest.
        //
        // A production somebody in this household is actually WORKING ON is
        // not one item in a chronology — it is the reason the screen was
        // opened. It used to sort purely by created_at, which put a booked
        // series third behind two reads that went nowhere, marked only by a
        // green outline nobody was looking for.
        //
        // Within each half it is still newest-first: a production a household
        // is in right now is the one they arrived to find.
        next.sort((a, b) => {
          const bookedA = a.lens === 'booked' ? 0 : 1;
          const bookedB = b.lens === 'booked' ? 0 : 1;
          if (bookedA !== bookedB) return bookedA - bookedB;
          return a.project.created_at < b.project.created_at ? 1 : -1;
        });
        setLines(next);
      } catch (err) {
        if (cancelled) return;
        setFailure(describeReadFailure(err));
        setLines(null);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [tenantId, retryNonce, recordsVersion]);

  if (failure) {
    return (
      <div class="sp">
        <div class="sp__eyebrow">Projects</div>
        <div class="sp__say">
          {failure.headline}
          <br />
          <span class="sp__say-soft">{failure.detail}</span>
        </div>
        {failure.retryable ? (
          <button type="button" class="sp__retry" onClick={() => setRetryNonce((n) => n + 1)}>
            Read the productions again
          </button>
        ) : null}
      </div>
    );
  }

  if (lines === null) {
    return (
      <div class="sp">
        <div class="sp__eyebrow">Projects</div>
        <div class="sp__say">One moment…</div>
      </div>
    );
  }

  const shown = lines.filter((l) => matchesLens(lens, l.lens));

  return (
    <div class="sp">
      <div class="sp__eyebrow">Projects</div>
      {/* The strip appears only once there is something to sort. One
          production does not need a filter, and a filter over one row reads
          as a control that does nothing. */}
      {lines.length > 1 ? (
        <Strip items={PROJECT_LENSES} activeId={lens} onSelect={(l) => setLens(l)} />
      ) : null}

      {shown.length === 0 ? (
        <div class="sp__empty">
          {/* A statement of what the system does with what arrives, never an
              instruction. A household with no productions has not failed to
              do something; it has not been sent a breakdown yet. */}
          {lines.length === 0
            ? 'A breakdown forwarded to your address starts a project here — its part, its dates, who’s casting.'
            : 'Nothing in this lens.'}
        </div>
      ) : (
        <div class="sp__tiles">
          {shown.map((line) => {
            const p = line.project;
            const booked = line.lens === 'booked';
            const hasPoster = typeof p.fields.poster_ref === 'string' && p.fields.poster_ref !== '';
            const isLogo = artIsALogo(p);
            const kindLabel =
              typeof p.fields.kind === 'string' && p.fields.kind
                ? humanizeValue(p.fields.kind)
                : 'Production';
            return (
              <button
                type="button"
                key={p.id}
                class={`sp__tile${booked ? ' sp__tile--booked' : ''}${
                  hasPoster && isLogo ? ' sp__tile--logo' : ''
                }${hasPoster && isLogo ? ` sp__tile-art--${artGround(p.fields.kind)}` : ''}${
                  hasPoster && isLogo && darkMarks[p.id] ? ' sp__tile--mark-dark' : ''
                }`}
                onClick={() => onOpen(p.id)}
              >
                {hasPoster ? (
                  // ⚑ THE POSTER IS A FILE FIELD ON THE RECORD, not a URL in
                  // it. `poster_ref` is declared `type: 'file'` on aa_project,
                  // so its value is a stored-file reference and the bytes come
                  // through the pack's own serving door — which is what
                  // RecordImage is (a presigned URL, fetched bare, painted as
                  // a blob). This screen used to put the raw field value
                  // straight into an <img src>, which can only ever have
                  // rendered a broken image.
                  <span class="sp__tile-fill">
                    <RecordImage
                      tenantId={tenantId}
                      recordId={p.id}
                      fieldName="poster_ref"
                      alt={`${p.display_name} — poster`}
                      onLoaded={
                        isLogo
                          ? (img) => {
                              const l = measureMarkLuminance(img);
                              if (l !== null && markIsDark(l)) setDarkMarks((d) => (d[p.id] ? d : { ...d, [p.id]: true }));
                            }
                          : undefined
                      }
                    />
                  </span>
                ) : (
                  // The title card. See spread.css on why this is a designed
                  // panel and not an empty box.
                  <span class={`sp__tile-art sp__tile-art--${artGround(p.fields.kind)}`}>
                    <span class="sp__tile-art-title">{p.display_name}</span>
                  </span>
                )}

                <span class="sp__tile-scrim">
                  {/* On a title card the name IS the art; repeating it in the
                      scrim under itself is the same word twice. A LOGO is not
                      a name, so the name is stated under it either way. */}
                  {hasPoster ? <span class="sp__tile-title">{p.display_name}</span> : null}
                  {/* kind · year · network · genre — what somebody says out
                      loud when they name a production ("the Amazon series").
                      The company used to sit on its own line at the foot of
                      the scrim, which spent a whole line of a small tile on
                      one of the four things that identify it. */}
                  <span class="sp__tile-meta">
                    {/* kind · year · network — and NOT the genre: on a phone
                        tile those three already fill the two lines the scrim
                        allows, so a genre only ever arrived as "· …" (the
                        showcase, 2026-09-09). The genre reads on the spine. */}
                    {[
                      kindLabel,
                      typeof p.fields.year === 'number' ? String(p.fields.year) : null,
                      typeof p.fields.company === 'string' && p.fields.company ? p.fields.company : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                  <span class="sp__tile-stand">
                    {booked ? <span class="sp__tile-booked">Booked</span> : null}
                    {booked && line.reads > 0 ? ' · ' : ''}
                    {line.reads > 0
                      ? `${line.reads} ${line.reads === 1 ? 'read' : 'reads'}`
                      : booked
                        ? ''
                        : `${line.roles} ${line.roles === 1 ? 'part' : 'parts'}`}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** WHICH TITLE-CARD GROUND A KIND GETS.
 *
 * Three grounds, and they are tone rather than hue — see spread.css on why a
 * fourth meaning-bearing colour is not available in this product. The mapping
 * is by what the thing IS to a reader, not by the picklist's order: a short
 * is a film, a limited series and a pilot are television, and everything the
 * pack does not sort into either (theatre, commercial, other, and a
 * production nobody has typed yet) takes the third.
 *
 * `kind` is optional on aa_project by declaration, so `undefined` is a normal
 * value here and not a gap. */
function artGround(kind: unknown): 'film' | 'serial' | 'stage' {
  if (kind === 'feature' || kind === 'short') return 'film';
  if (kind === 'series' || kind === 'limited_series' || kind === 'pilot') return 'serial';
  return 'stage';
}

/** IS THE ART A MARK RATHER THAN A PICTURE?
 *
 * Wikidata's image for a production is a Commons still more often than a
 * one-sheet, and for a series it is frequently the network's LOGO on a
 * transparent ground (P154). A logo cropped to fill a 2:3 tile is a logo with
 * its edges cut off, so it is contained instead — see spread.css.
 *
 * ⚠ THE FIELD IS NOT DECLARED YET. The lookup build (agent S) sets the flag;
 * this reads `poster_role === 'logo'` and answers false for a record that
 * carries nothing, which is every record today. That is the honest default:
 * an unflagged image is treated as a picture, which is what a still is. One
 * line changes if the flag lands under another name. */
function artIsALogo(project: RecordRow): boolean {
  return project.fields.poster_role === 'logo';
}

